import { Component, EventEmitter, inject, Input, OnInit, Output, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonTypeJobPlan } from '@shared/models/enums/button-type-job-plan.enum';
import { JobFlowType } from '@shared/models/enums/job-flow-type.enum';
import { ContainerSummary } from '@control-tower/models';
import { JobFile } from '@core/models/job-file';
import { Jpm } from '@control-tower/models/jpm';
import { JobStatusEnum } from 'app/modules/dashboard/models/job-summary.model';
import { Store } from '@ngxs/store';
import { AuthState } from '@core/store/auth/auth.state';
import { FirebaseService } from '@core/services/firebase.service';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'hmt-job-planning-footer-bar',
  templateUrl: './job-planning-footer-bar.component.html',
  standalone: false,
  styleUrls: ['./job-planning-footer-bar.component.scss']
})
export class JobPlanningFooterBarComponent implements OnInit, OnChanges, OnDestroy {
 
  activateRoute = inject(ActivatedRoute);
  router = inject(Router);

  steps: string[] = [];
  @Input() activeStepIndex = 0;
  @Input() isOptimizing = false;
  @Input() showOptimizeButton = false;
  @Input() buttonType: ButtonTypeJobPlan = ButtonTypeJobPlan.OPTIMIZED;
  @Input() showRequestTab = false;
  @Input() showPrimaryButton = false;
  @Input() showSecondaryButton = false;
  @Input() primaryButtonText: string | null = null;
  @Input() secondaryButtonText: string | null = null;
  @Input() isPrimaryButtonDisabled = false;
  @Input() showControlTowerButtons = false;
  @Input() confirmedContainers: ContainerSummary[] = [];
  @Input() isDisabled = false;
  @Input() isReadOnly = false;
  /** Job was terminated at the planning stage: Optimize / Proceed are locked. */
  @Input() isJobTerminated = false;
  @Input() jobFile: JobFile;
  @Input() jpms: Jpm[] = [];
  @Input() flowType: JobFlowType = JobFlowType.BUYER;
  @Output() stepSelected = new EventEmitter<{index: number, step: string}>();
  @Output() primaryButtonClicked = new EventEmitter<void>();
  @Output() secondaryButtonClicked = new EventEmitter<void>();
  @Output() viewDocuments = new EventEmitter<{containerSummary: ContainerSummary[], isReadOnly: boolean}>();
  @Output() terminateJob = new EventEmitter<void>();
  @Output() openUpdateInvoiceInfo = new EventEmitter<void>();
  @Output() containerTerminate = new EventEmitter<void>();
  @Output() invoiceGenerate = new EventEmitter<string>();
  @Output() jobSummaryClicked = new EventEmitter<void>();
  
  // Local state to track if button has been clicked once in job summary (permanent disable)
  private hasPrimaryButtonBeenClickedOnce = false;

  // Index of Planning step (used to insert Job Summary button after it)
  planningStepIndex: number = -1;

  // Tracks whether invoice/payment info is currently being generated (drives spinner + disabled state)
  isLoadingInvoiceInfo = false;
  private jobRefId: string;
  private firebaseSubscription: any;
  private unsubscribe = new Subject<void>();

  constructor(private store: Store, private firebaseService: FirebaseService) {
  }
  setActiveStep(index: number, step: string): void {
    console.log('footer jobfile', this.jobFile);
    if (this.isStepLocked(index)) {
      return;
    }
    if ((index < 0 || index >= this.steps.length) || (index > this.activeStepIndex) && (JobFlowType.SELLER !== this.flowType)) {
      if(this.flowType === JobFlowType.BUYER && this.jobFile) {
        if(this.jobFile.containers.length > 0 && this.jobFile?.jobSummaryPDFRef != null) {
          if (step === 'Planning') {
            this.router.navigate(['/load-management'], { queryParams: { jobRefId: this.jobFile?.jobRefId } });
         } else if (step === 'Load Assignment') {
          this.router.navigate(['/load-assignment'], { queryParams: { jobRefId: this.jobFile?.jobRefId } });
         } else if(step === 'Control Tower' && this.jobFile?.jobSummaryPDFRef != null && (this.jobFile?.status?.toUpperCase() === 'COMPLETED' || this.jobFile?.status?.toUpperCase() === 'PARTIALLY_COMPLETED')) {
          this.router.navigate(['/control-tower'], { queryParams: { jobRefId: this.jobFile?.jobRefId } });
         }
        }
      }
      return;
    }
    this.stepSelected.emit({index: index, step: step});
  }

  /**
   * True when the job never got past planning and at least one of its loads was terminated there:
   * every load is still in planning or was terminated in planning (preTerminationJobProgressStatus).
   * Such a job cannot move on from Load Assignment.
   */
  isLockedAtLoadAssignment(): boolean {
    const containers = this.jobFile?.containers ?? [];
    const isTerminated = (status?: string) => (status || '').toUpperCase() === 'TERMINATED';
    const terminatedInPlanning = containers.some(container =>
      isTerminated(container?.jobProgressStatus)
      && (container?.preTerminationJobProgressStatus || '').toUpperCase() === 'PLANNING');
    if (!terminatedInPlanning) {
      return false;
    }
    return containers.every(container => {
      const status = isTerminated(container?.jobProgressStatus)
        ? container?.preTerminationJobProgressStatus
        : container?.jobProgressStatus;
      return !status || status.toUpperCase() === 'PLANNING';
    });
  }

  /** Steps after Load Assignment cannot be opened while the job is locked at Load Assignment. */
  isStepLocked(index: number): boolean {
    const loadAssignmentIndex = this.steps?.indexOf('Load Assignment') ?? -1;
    return loadAssignmentIndex >= 0 && index > loadAssignmentIndex && this.isLockedAtLoadAssignment();
  }

  getPrimaryButtonText() {
    if(this.primaryButtonText) {
      return this.primaryButtonText;
    }

    return this.isJobSummary() ? 'Confirm' : 'Proceed';
  }

  getSecondaryButtonText() {
    if(this.secondaryButtonText) {
      return this.secondaryButtonText;
    }

    return 'Get Artifacts';
  }

  onPrimaryButtonClick(): void {
    // For job summary, disable button permanently after first click
    if (this.isJobSummary()) {
      this.hasPrimaryButtonBeenClickedOnce = true;
    }
    this.primaryButtonClicked.emit();
  }

  /**
   * Gets the disabled state for the primary button
   * For job summary, disables permanently after first click; otherwise uses input
   */
  getPrimaryButtonDisabled(): boolean {
    if (this.isJobTerminated) {
      return true;
    }
    if (this.isJobSummary()) {
      return this.isPrimaryButtonDisabled || this.hasPrimaryButtonBeenClickedOnce;
    }
    return this.isPrimaryButtonDisabled;
  }
  onSecondaryButtonClick(): void {
    this.secondaryButtonClicked.emit();
  }

  onTerminateLoadClick(): void {
    this.containerTerminate.emit();
  }

  onViewDocumentsClick(): void {
    const result = {
      containerSummary: this.confirmedContainers,
      isReadOnly: this.isReadOnly
    };
    this.viewDocuments.emit(result);
  }

  onTerminateJobClick(): void {
    this.terminateJob.emit();
  }

  onGenerateInvoiceClick(): void {
    const jobRefId = this.jpms && this.jpms.length > 0 ? this.jpms[0]?.jobRefId : null;
    if (jobRefId) {
      this.invoiceGenerate.emit(jobRefId);
    }
  }

  onViewUpdateInvoiceInfoClick(): void {
    this.openUpdateInvoiceInfo.emit();
  }

  isViewUpdateInvoiceInfoDisabled(): boolean {
    return this.isReadOnly || this.isLoadingInvoiceInfo;
  }

  private setupFirebaseListener(): void {
    if (this.firebaseSubscription) {
      this.firebaseSubscription.unsubscribe();
      this.firebaseSubscription = null;
    }

    const user = this.store.selectSnapshot(AuthState.getUser);
    if (!user || !user.orgId) {
      return;
    }

    if (!this.jobRefId) {
      return;
    }

    const orgId = user.orgId;
    const userId = user.orgUserId || user.userId;

    if (!userId) {
      return;
    }

    // @ts-ignore - TypeScript has issues with chained pipe on Firebase observable
    this.firebaseSubscription = this.firebaseService
      .listenToNotifications(
        orgId,
        'PROGRESS_MONITOR', // notificationType
        'COMMON', // collectionId
        'UPDATE_INVOICE_INFO', // eventType
        `loading-${this.jobRefId}`, // eventId - dynamic based on jobRefId
        undefined // Listen to entire collection
      )
      .pipe(
        // @ts-ignore
        tap((firebaseData: any) => {
          if (Array.isArray(firebaseData) && firebaseData.length > 0) {
            const allData = firebaseData.map((change: any) => {
              return change.payload?.doc?.data() || change.payload || change;
            }).filter(data => data != null);

            let latestData = null;
            let latestTimestamp = null;

            allData.forEach((data: any) => {
              if (data.createdAt) {
                const timestamp = data.createdAt.seconds ||
                                 (data.createdAt.toMillis ? data.createdAt.toMillis() / 1000 : null) ||
                                 (data.createdAt instanceof Date ? data.createdAt.getTime() / 1000 : null);

                if (timestamp && (!latestTimestamp || timestamp > latestTimestamp)) {
                  latestTimestamp = timestamp;
                  latestData = data;
                }
              } else if (!latestData) {
                latestData = data;
              }
            });

            if (latestData && latestData.loading !== undefined) {
              this.isLoadingInvoiceInfo = latestData.loading === true;
            }
          }

          if (firebaseData && firebaseData.payload && !Array.isArray(firebaseData)) {
            const data =
              firebaseData.payload.data?.() ||
              firebaseData.payload ||
              firebaseData;

            if (data && data.loading !== undefined) {
              this.isLoadingInvoiceInfo = data.loading === true;
            }
          }
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        error: (error: any) => {
          if (error?.code !== 'not-found' && error?.code !== 'permission-denied') {
            console.error('Error listening to Firebase loading state:', error);
          }
          this.isLoadingInvoiceInfo = false;
        }
      });
  }

  isGenerateInvoiceDisabled(): boolean {
    if (!this.jpms || this.jpms.length === 0) {
      return true;
    }
    // Invoice can be generated only when all JPMs are completed
    return !this.jpms.every(jpm => jpm.isCompleted) || this.isReadOnly;
  }


  displayOptimizeButton(): boolean {
    return this.buttonType === ButtonTypeJobPlan.OPTIMIZED || this.showOptimizeButton;
  }
  displaySecondaryButton(): boolean {
    return this.buttonType === ButtonTypeJobPlan.LOAD_MANAGEMENT || this.showSecondaryButton;
  }
  displayPrimaryButton(): boolean {
    return this.buttonType === ButtonTypeJobPlan.LOAD_MANAGEMENT || this.buttonType === ButtonTypeJobPlan.JOB_SUMMARY || this.showPrimaryButton || this.buttonType === ButtonTypeJobPlan.SCHEDULE;
  }

  isJobSummary(): boolean {
    return this.buttonType === ButtonTypeJobPlan.JOB_SUMMARY;
  }

  isSchedule(): boolean {
    return this.buttonType === ButtonTypeJobPlan.SCHEDULE;
  }

  ngOnInit() {
    this.initializeSteps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['flowType'] || changes['showRequestTab']) {
      this.initializeSteps();
    }
    if (changes['jpms'] && this.jpms && this.jpms.length > 0) {
      this.jobRefId = this.jpms[0]?.jobRefId;
      this.setupFirebaseListener();
    }
  }

  ngOnDestroy(): void {
    if (this.firebaseSubscription) {
      this.firebaseSubscription.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**
   * Initialize steps based on flow type
   */
  private initializeSteps(): void {

    switch (this.flowType) {
      case JobFlowType.BUYER:
        // Buyer flow: Planning -> [Job Summary Button] -> Load Assignment -> Schedule -> Supplier Contract -> Control Tower
        this.steps = ['Planning', 'Load Assignment', 'Schedule', 'Supplier Contract', 'Control Tower'];
        this.planningStepIndex = 0;
        break;
      
      case JobFlowType.SELLER:
        // if (this.showRequestTab) {
          this.steps = ['Request', 'Planning', 'Load Assignment', 'Customer Contract', 'Supplier Contract', 'Schedule', 'Control Tower'];
          this.planningStepIndex = 1; // Planning is at index 1 (after Request)
        // } else {
        //   this.steps = ['Planning', 'Load Assignment', 'Customer Contract', 'Supplier Contract', 'Schedule', 'Control Tower'];
        //   this.planningStepIndex = 0; // Planning is at index 0
        // }
        break;
      
      case JobFlowType.ONBEHALF:
        // Onbehalf flow: Planning -> Load Assignment -> Customer Contract -> Supplier Contract -> Schedule -> Control Tower
        // Note: Request tab should NOT appear in onbehalf flow
        this.steps = ['Planning', 'Load Assignment', 'Customer Contract', 'Supplier Contract', 'Schedule', 'Control Tower'];
        this.planningStepIndex = 0;
        break;
      
      default:
        // Default to buyer flow
        this.steps = ['Planning', 'Load Assignment', 'Schedule', 'Supplier Contract', 'Control Tower'];
        this.planningStepIndex = 0;
    }
  }

  /**
   * Check if Job Summary button should be shown (only for buyer flow, between Planning and Load Assignment)
   */
  shouldShowJobSummaryButton(): boolean {
    return this.flowType === JobFlowType.BUYER && this.planningStepIndex >= 0;
  }

  /**
   * Check if Job Summary button should be shown in footer buttons area (when Planning is active)
   */
  shouldShowJobSummaryInFooter(): boolean {
    return this.flowType === JobFlowType.BUYER && 
           this.planningStepIndex >= 0 && 
           this.activeStepIndex === this.planningStepIndex;
  }

  /**
   * Navigate to job summary page
   */
  onJobSummaryClick(): void {
    if (this.jobFile?.jobRefId) {
      this.router.navigate(['/job-summary'], {
        queryParams: { jobRefId: this.jobFile.jobRefId }
      });
      this.jobSummaryClicked.emit();
    }
  }

  /**
   * Get the step index where Job Summary button should appear
   * Returns the index after Planning step
   */
  getJobSummaryButtonIndex(): number {
    return this.planningStepIndex + 1;
  }

  /**
   * Check if a step is the Job Summary button position
   */
  isJobSummaryButtonPosition(stepIndex: number): boolean {
    return this.shouldShowJobSummaryButton() && stepIndex === this.getJobSummaryButtonIndex();
  }

  /**
   * Get step name at index, accounting for Job Summary button position
   */
  getStepName(index: number): string {
    if (this.isJobSummaryButtonPosition(index)) {
      return 'Job Summary';
    }
    // Adjust index if we're past the Job Summary button position
    const adjustedIndex = this.shouldShowJobSummaryButton() && index > this.getJobSummaryButtonIndex() 
      ? index - 1 
      : index;
    return this.steps[adjustedIndex] || '';
  }

}
